import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router';
import { ActivityTimeline } from '../activity/ActivityTimeline';
import { NO_ACTIVITY_FILTER, type ActivityFilterState, type ActivityTargets } from '../activity/describe';
import { useApi } from '../api/context';
import { queryKeys, useActiveRuns } from '../api/queries';
import { isTerminalRunStatus, type Run } from '../api/types';
import { runStatusPresentation } from '../components/presentation';
import { Loading, StatusBadge } from '../components/ui';
import { ToolWindowContent } from '../dock/ToolWindowContent';
import { formatClock } from '../lib/format';
import { strings } from '../strings';
import { EventLogView } from './EventLogView';
import { useRunEventLog } from './useRunEventLog';

const s = strings.activity;

function RunActivity({ runId, showLink }: { runId: string; showLink: boolean }) {
  const api = useApi();
  const queryClient = useQueryClient();
  const runQuery = useQuery({ queryKey: queryKeys.run(runId), queryFn: () => api.getRun(runId) });
  const run = runQuery.data;
  const { log, stream } = useRunEventLog(runId, run, (completed: Run) => {
    queryClient.setQueryData(queryKeys.run(completed.id), completed);
    void queryClient.invalidateQueries({ queryKey: ['deployment', completed.deploymentId] });
  });
  if (!run) return <Loading />;
  const terminal = isTerminalRunStatus(run.status);
  const status = !terminal && log.runStatus ? log.runStatus : run.status;
  const progress = (!terminal && log.progress) || run.progress;
  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="tw-toolbar">
        <span className="font-semibold">{s.run(strings.status.runKind[run.kind])}</span>
        <StatusBadge presentation={runStatusPresentation[status]} />
        {stream === 'reconnecting' && <span className="badge tone-warning">{strings.runs.reconnecting}</span>}
        {progress && (
          <span className="text-fg-muted">
            {strings.runs.progressValue(progress.completed, progress.total)}
          </span>
        )}
        {run.startedAt && <span className="text-fg-muted">{s.started(formatClock(run.startedAt))}</span>}
        {showLink && (
          <Link className="link ml-auto" to={`/runs/${run.id}`}>
            {s.openRun}
          </Link>
        )}
      </div>
      <div className="flex min-h-0 flex-1 flex-col p-2">
        <EventLogView events={log.events} fill />
      </div>
    </div>
  );
}

/**
 * The Activity window on the lists (spec 6.8.11): the installation's active runs from the polled run summaries
 * shared with the active-runs pill; it opens no event streams.
 */
export function ActiveRunsActivity() {
  const { data, error } = useActiveRuns();
  if (error) return <p className="p-3">{strings.common.unknownError}</p>;
  if (!data) return <Loading />;
  if (data.items.length === 0) {
    return (
      <div className="p-3">
        <p>{s.nothingRunning}</p>
        <Link className="link mt-1 inline-block" to="/runs">
          {s.allRuns}
        </Link>
      </div>
    );
  }
  return (
    <div className="min-h-0 flex-1 overflow-auto p-2">
      <ul aria-label={s.activeRuns} className="flex flex-col gap-1">
        {data.items.map((run) => (
          <li key={run.id} className="flex flex-wrap items-center gap-2 rounded-md px-2 py-1 hover:bg-hover">
            <Link className="link font-medium" to={`/runs/${run.id}`}>
              {strings.shell.activeRuns.item(
                strings.status.runKind[run.kind],
                run.deploymentName,
                run.blueprintName,
              )}
            </Link>
            <StatusBadge presentation={runStatusPresentation[run.status]} />
            {run.progress && run.progress.total > 0 && (
              <span className="font-mono text-[12px] text-fg-muted">
                {strings.runs.progressValue(run.progress.completed, run.progress.total)}
              </span>
            )}
            {run.startedAt && <span className="text-fg-muted">{s.started(formatClock(run.startedAt))}</span>}
          </li>
        ))}
      </ul>
    </div>
  );
}

/** Provides the Activity window's content on the list pages (spec 6.8.2): the active runs. */
export function ListActivityWindow() {
  const { data } = useActiveRuns();
  return (
    <ToolWindowContent window="activity" badge={{ live: (data?.items.length ?? 0) > 0 }}>
      <ActiveRunsActivity />
    </ToolWindowContent>
  );
}

/** A deployment without an active run: its activity timeline (spec 6.8.11, 6.11). */
function DeploymentActivity({
  deployment,
  resources,
  targets,
}: {
  deployment: { id: string; name: string };
  resources: readonly string[];
  targets?: ActivityTargets;
}) {
  const [filter, setFilter] = useState<ActivityFilterState>(NO_ACTIVITY_FILTER);
  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="tw-toolbar">
        <span>{s.noActiveRun(deployment.name)}</span>
        <Link className="link ml-auto" to={`/deployments/${deployment.id}?tab=activity`}>
          {s.openActivityTab}
        </Link>
      </div>
      <ActivityTimeline
        compact
        deployment={deployment}
        filter={filter}
        onFilterChange={setFilter}
        resources={resources}
        targets={targets}
      />
    </div>
  );
}

/**
 * The Activity tool window (spec 6.8.11): the log of a deployment's active run, or of the run page's run,
 * through the shared event subscription; without an active run, the deployment's activity timeline.
 */
export function ActivityWindow({
  runId,
  deployment,
  resources = [],
  onRunPage = false,
  targets,
}: {
  runId: string | null;
  deployment: { id: string; name: string } | null;
  /** The deployment's resources, for the timeline's Resource filter. */
  resources?: readonly string[];
  onRunPage?: boolean;
  /** The timeline's targets that can be shown (spec 6.11.3). */
  targets?: ActivityTargets;
}) {
  if (runId) return <RunActivity key={runId} runId={runId} showLink={!onRunPage} />;
  if (deployment)
    return (
      <DeploymentActivity
        key={deployment.id}
        deployment={deployment}
        resources={resources}
        targets={targets}
      />
    );
  return <p className="p-3">{s.noActiveRun('')}</p>;
}
